
import { defineComponent } from "vue";
import { Rank } from "@/store/Ranks/RanksInterfaces";
import { mapGetters } from "vuex";
import {
  images,
  getCachedImageUrl,
  getCachedProfilePictureUrl,
} from "@/mixins/images";

import {
  setLeaderUsername,
  UnilevelMember,
} from "@/store/Unilevel/UnilevelInterfaces";
import {
  ACADEMY_LICENSE_IDS,
  ACADEMY_SUBSCRIPTION_IDS,
  LITE_LICENSE_IDS,
  LITE_SUBSCRIPTION_IDS,
  maxRemainingDaysOf,
  normalizePackageLabel,
  PKG_FLY,
  PKG_GO,
  PKG_WIN,
} from "functions/src/Subscriptions/packageIds";
import PackageIcon from "../common/PackageIcon.vue";

export default defineComponent({
  name: "team-member",
  components: {
    PackageIcon,
  },
  emits: ["refresh"],
  props: {
    memberData: { required: true, type: Object as () => UnilevelMember },
    teamSide: { type: String, required: true },
    maxPointsPerUser: { type: Number, required: true },
  },
  data() {
    return {
      rankBackground: "",
      profilePic: "",
      refreshing: false,
    };
  },
  computed: {
    ...mapGetters("ranks", ["rankById"]),
    userRank(): Rank {
      return this.rankById(this.memberData.currentRank);
    },
    // Color de tier del rango (mismo token --rank-<id> que usa RanksCard); gris
    // de respaldo si el rango no tiene token (datos legacy / rango desconocido).
    rankColor(): string {
      return `var(--rank-${this.userRank?._id}, var(--gtr-mid, #5a5a5a))`;
    },
    picSideClass(): string {
      return this.teamSide == "left" ? "order-2" : "order-1";
    },
    dataSideClass(): string {
      return this.teamSide == "left" ? "order-1" : "order-2";
    },
    bgSideClass(): string {
      return this.teamSide == "left" ? "order-2" : "order-1";
    },
    photoSideClass(): string {
      return this.teamSide == "left" ? "order-1" : "order-2";
    },
    memberShortcutsClass(): string {
      return this.teamSide == "left" ? "order-2" : "order-1";
    },
    memberBriefClass(): string {
      return this.teamSide == "left" ? "order-1" : "order-2";
    },
    remainigDaysTradingAcademy(): number {
      // La academia en sus DOS generaciones, la que más días deje. Con sólo el
      // documento de siempre, el resultado es idéntico al de antes.
      return (
        maxRemainingDaysOf(
          this.memberData.subscriptions,
          ACADEMY_SUBSCRIPTION_IDS
        ) || 0
      );
    },
    remainigDaysDistributor(): number {
      // La de siempre O la GLOBAL, la que más días deje. SIN rama por paquete,
      // igual que antes de este cambio: esta tarjeta miraba `distributor-license`
      // para todo el mundo y sigue igual, sólo que ahora la global también vale.
      return this.memberData.subscriptions?.get
        ? Math.max(
            ...ACADEMY_LICENSE_IDS.map(
              (id) => this.memberData.subscriptions?.get(id) || 0
            )
          )
        : 0;
    },
    isVIP(): boolean {
      let isVIP = false;
      try {
        // If subscriptions is a Map
        isVIP = this.memberData?.subscriptions.has("mmi-colombia") ?? false;
      } catch {
        // If subscriptions is an object
        isVIP = this.memberData?.subscriptions["mmi-colombia"] ?? false;
      }
      return isVIP;
    },
    gtrLiteDaysLeft(): number | undefined {
      // Gemela de la de academia, con la familia del Lite. El `undefined` (no
      // tiene ninguna) SE CONSERVA: la plantilla lo distingue de un 0.
      return maxRemainingDaysOf(
        this.memberData.subscriptions,
        LITE_SUBSCRIPTION_IDS
      );
    },
    liteDistributorDaysLeft(): number | undefined {
      // La licencia del Lite es una FAMILIA desde que existe la GLOBAL: la suya
      // o `gtr-distributor-license`, la que más días deje. Leyendo sólo la suya,
      // un Lite con la global salía sin licencia.
      return maxRemainingDaysOf(
        this.memberData.subscriptions,
        LITE_LICENSE_IDS
      );
    },
    /** El nivel del miembro, traducido al vocabulario vivo (ver CarrerSummaryTable). */
    currentUserPackage(): string {
      return (
        normalizePackageLabel(
          this.memberData.subscriptionPackage.package.packageId
        ) || ""
      );
    },
    isAcademyLevel(): boolean {
      return (
        this.currentUserPackage === PKG_FLY ||
        this.currentUserPackage === PKG_WIN
      );
    },
    isLite(): boolean {
      return this.currentUserPackage === PKG_GO;
    },
    /**
     * EL FONDO DE LA TARJETA DICE QUÉ ES ESTE SOCIO.
     *
     * Tres casos: tiene el Acelerador de Riqueza (dorado), es un Go (lavado
     * teal) o no es ninguna de las dos (el gris de siempre).
     *
     * El del Go era `lite-background-color`, un azul claro (#dbf3ff) escrito a
     * mano en el CSS global que no tenía relación con ningún color del sistema
     * — decía «Go» por costumbre, no porque fuera el color de Go. Ahora es un
     * lavado del propio token de GO (ver `.member-card-go` abajo): el día que
     * cambie el color del paquete, cambia también esta tarjeta.
     *
     * El dorado del Acelerador NO se toca y NO es el de Win, aunque el tono se
     * parezca: el Acelerador no es un escalón de la escalera de paquetes.
     */
    backgroundColor(): string {
      return this.isVIP
        ? "vip-background-color"
        : this.isLite
        ? "member-card-go"
        : "bg-light";
    },
  },
  methods: {
    changeTeamLeader(newLeaderUsername: string) {
      if (newLeaderUsername != undefined) {
        this.$store.dispatch(
          "unilevel/" + setLeaderUsername,
          newLeaderUsername
        );
      }
    },
    refreshMember() {
      if (this.refreshing) return;
      this.refreshing = true;
      this.$emit("refresh", this.memberData, () => {
        this.refreshing = false;
      });
    },
    // Carga el fondo del rango. Se llama en created() y de nuevo cuando
    // `userRank` resuelve (el store de rangos puede cargar DESPUÉS de montar
    // el tile en frío, dejándolo undefined al inicio).
    loadRankBackground() {
      const bgPath = this.userRank?.backgroundImage;
      if (!bgPath) return;
      const cachedBg = getCachedImageUrl(bgPath);
      if (cachedBg) {
        this.rankBackground = cachedBg;
      } else {
        this.getImage(bgPath).then((response) => {
          this.rankBackground = response;
        });
      }
    },
  },
  watch: {
    memberData() {
      this.refreshing = false;
    },
    userRank() {
      // el store de rangos puede resolver tras montar el tile en frío;
      // recargar el fondo cuando userRank quede disponible (antes created()
      // leía userRank.backgroundImage sin guardas y reventaba → tile en
      // blanco + avatar roto, sin reintento).
      this.loadRankBackground();
    },
  },
  created() {
    this.loadRankBackground();

    const cachedPic = getCachedProfilePictureUrl(this.memberData.pictureUrl);
    if (cachedPic) {
      this.profilePic = cachedPic;
    } else {
      this.getProfilePicture(this.memberData.pictureUrl).then((response) => {
        this.profilePic = response;
      });
    }
  },
  setup() {
    const getProfilePicture = images.methods.getProfilePicture;
    const getImage = images.methods.getImage;
    return {
      getProfilePicture,
      getImage,
    };
  },
});
